import React from 'react';
import { Box } from '@mui/material';
import { Typography } from '@occc/ui-design-system';
import { readableNumber } from '@occc-app/lib/format';

import { MetricSnapshot } from './MetricSnapshot';
import { Metrics } from '../types';
import { NUMERIC_ABBREVIATIONS } from '../constants';

type RevenueSnapshotsProps = {
  metrics: Metrics;
  interval: string;
};

export const RevenueSnapshots = ({ interval, metrics }: RevenueSnapshotsProps) => {
  const calculateAverageBasketMetric = () => {
    const calculateAverage = (num: number, denom: number) => (denom === 0 ? 0 : num / denom);
    return {
      interval: metrics.revenue.interval,
      dataPoints: [],
      subtotal: calculateAverage(
        metrics.revenue.subtotal / 100,
        metrics.transactionCreated.subtotal,
      ),
      priorSubtotal: calculateAverage(
        metrics.revenue.priorSubtotal / 100,
        metrics.transactionCreated.priorSubtotal,
      ),
      total: calculateAverage(metrics.revenue.total / 100, metrics.transactionCreated.total),
    };
  };

  const abbreviateNumber = (value: number) => {
    const matchingAbbr = NUMERIC_ABBREVIATIONS.find((abbr) => value >= abbr.minValue);
    if (!matchingAbbr) {
      return { value, suffix: '' };
    }
    return {
      value: Math.round((value / matchingAbbr.minValue) * 10) / 10,
      suffix: matchingAbbr.abbreviation,
    };
  };

  const formatRevenue = (value: number) => {
    const abbreviation = abbreviateNumber(value / 100);
    return `$${readableNumber(abbreviation.value)} ${abbreviation.suffix}`;
  };

  const formatAverageBasket = (value: number) => {
    return `$${readableNumber(value)}`;
  };

  const formatPointsEarned = (value: number) => {
    const abbreviation = abbreviateNumber(value);
    return `${readableNumber(abbreviation.value)} ${abbreviation.suffix}`;
  };

  const formatPointsBurned = (value: number) => {
    const abbreviation = abbreviateNumber(-value);
    return `${readableNumber(abbreviation.value)} ${abbreviation.suffix}`;
  };

  if (interval === 'hour') {
    return (
      <Box p={4}>
        <Typography variant='body1'>
          Revenue metrics are not available for the selected time range.
        </Typography>
      </Box>
    );
  }

  return (
    <Box display='flex' mx={-3} flexWrap='wrap'>
      <Box width={[1, 1 / 2, 1 / 4]} flex='1'>
        <MetricSnapshot
          label='Loyalty Revenue'
          metric={metrics.revenue}
          formatValue={formatRevenue}
        />
      </Box>
      <Box width={[1, 1 / 2, 1 / 4]} flex='1'>
        <MetricSnapshot
          label='Average Basket'
          metric={calculateAverageBasketMetric()}
          formatValue={formatAverageBasket}
        />
      </Box>
      <Box width={[1, 1 / 2, 1 / 4]} flex='1'>
        <MetricSnapshot
          label='Points Earned'
          metric={metrics.pointsEarned}
          formatValue={formatPointsEarned}
        />
      </Box>
      <Box width={[1, 1 / 2, 1 / 4]} flex='1'>
        <MetricSnapshot
          label='Points Burned'
          metric={metrics.pointsBurned}
          formatValue={formatPointsBurned}
        />
      </Box>
    </Box>
  );
};
